<template>
  <model-form
    ref="modelForm"
    :model="globalWidget"
    :handled-actions="handledActions"
    :is-dialog="isDialog"
    :title="globalWidget.name ? `Editing Global ${globalWidget.type}` : 'New Global Widget'"
    disable-language-select
    show-translation-notice
    :none-padding="compactView"
    :hide-header-buttons="showDrawerButton"
    @change="$emit('change')"
  >
    <q-tabs
      v-if="tabView"
      v-model="tab"
      class="q-mb-sm"
      dense
      active-color="primary"
      indicator-color="primary"
      align="justify"
    >
      <q-tab
        name="Content"
        label="Content"
      />
      <q-tab
        name="style"
        label="Visibility & Style"
      />
    </q-tabs>

    <div
      v-if="!tabView || tab === 'Content'"
      :styles="baseStyles"
      :class="{ 'q-px-sm': compactView }"
    >
      <div
        class="row items-start"
        :class="compactView ? 'q-col-gutter-x-sm q-col-gutter-y-sm' : 'q-col-gutter-md'"
      >
        <q-select
          v-model="globalWidget.type"
          emit-value
          map-options
          :class="compactView ? 'col-auto q-pb-sm' : 'col-6'"
          label="Type"
          :options="globalWidget.fieldOptions('type')"
          :rules="[val => val !== null || 'Field is required']"
        />
        <model-form-input
          field="name"
          class="col"
          :class="compactView ? 'q-pb-sm' : undefined"
          label="Name"
          :rules="[val => !!val || 'Field is required']"
        />
        <div
          v-if="!tabView"
          class="col-auto q-mt-sm"
        >
          <q-btn
            icon="settings"
            :dense="compactView"
            :disable="globalWidget.isTranslation"
            @click="$refs.genericGlobalWidgetSettings.show()"
          >
            <english-required-tooltip v-if="globalWidget.isTranslation" />
            <q-tooltip v-else>
              Visibility Settings
            </q-tooltip>
          </q-btn>
        </div>
      </div>
      <global-widget-banner-editor
        v-if="globalWidget.type === GLOBAL_WIDGET_TYPES.BANNER"
        :global-widget="globalWidget"
        :compact-view="compactView"
      />
      <global-widget-footer-editor
        v-else-if="globalWidget.type === GLOBAL_WIDGET_TYPES.FOOTER"
        :global-widget="globalWidget"
        :compact-view="compactView"
      />
      <q-banner
        v-if="!!foundNetlifyUrl"
        dense
        rounded
        class="bg-orange text-white col-12 q-mb-sm"
      >
        <template #avatar>
          <q-icon name="warning" />
        </template>
        Global widget content contains {{ foundNetlifyUrl }}
      </q-banner>
      <div v-if="!hidePreview">
        <global-widget
          :global-widget="globalWidget"
          is-editing
        />
      </div>
    </div>
    <global-widget-generic-settings-form
      v-if="tab === 'style' || !tabView"
      ref="genericGlobalWidgetSettings"
      :global-widget="globalWidget"
      :compact-view="compactView"
      :dialog="isDialog"
    />
    <div
      v-if="showDrawerButton"
      class="row fixed-bottom full-width"
    >
      <q-btn-group
        class="full-width"
        spread
      >
        <q-btn
          label="View & Save"
          color="primary"
          :text-color="$getColor(currentSite, 'primary')"
          @click="$refs.modelForm.save()"
        />
        <q-btn
          label="Cancel"
          color="negative"
          :text-color="$getColor(currentSite, 'negative')"
          @click="cancel()"
        />
      </q-btn-group>
    </div>
  </model-form>
</template>

<script>
import { GLOBAL_WIDGET_TYPES } from 'src/models/global-widget/GlobalWidget'
import EnglishRequiredTooltip from 'src/modules/admin/components/EnglishRequiredTooltip'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import GlobalWidgetBannerEditor from 'src/modules/admin/global-widget/editors/GlobalWidgetBannerEditor'
import GlobalWidgetFooterEditor from 'src/modules/admin/global-widget/editors/GlobalWidgetFooterEditor'
import GlobalWidgetGenericSettingsForm from 'src/modules/admin/global-widget/GlobalWidgetGenericSettingsForm'
import GlobalWidget from 'src/modules/consumer/components/global-widget/GlobalWidget'

export default {
  name: 'GlobalWidgetForm',
  components: {
    EnglishRequiredTooltip,
    ModelForm,
    GlobalWidgetGenericSettingsForm,
    GlobalWidget,
    GlobalWidgetBannerEditor,
    GlobalWidgetFooterEditor
  },
  configuration: ['currentSite'],
  props: {
    handledActions: {
      type: Array,
      required: false,
      default () {
        return ['add', 'edit']
      }
    },
    isDialog: {
      type: Boolean,
      required: false,
      default: true
    },
    hidePreview: {
      type: Boolean,
      required: false,
      default: false
    },
    compactView: {
      type: Boolean,
      required: false,
      default: false
    },
    tabView: {
      type: Boolean,
      required: false,
      default: false
    },
    showDrawerButton: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['change', 'cancel'],
  models: {
    globalWidget: {
      globalWidgetBlocks: {
        navigationIds: true,
        navigations: {
          page: {
            fields: ['id', 'slug']
          }
        },
        bannerHeaderLinkableId: true,
        bannerHeaderLinkableType: true,
        bannerHeaderLinkableName: true,
        bannerHeaderLinkablePath: true,
        bannerBodyLinkableId: true,
        bannerBodyLinkableType: true,
        bannerBodyLinkableName: true,
        bannerBodyLinkablePath: true
      },
      visiblePages: {
        fields: ['id', 'title']
      },
      visiblePageIds: true,
      content: true,
      styles: true,
      transformedStyles: true,
      scopeClass: true,
      libraryChildSiteIds: true
    }
  },
  data () {
    return {
      showStartCalendar: false,
      showEndCalendar: false,
      tab: 'Content'
    }
  },
  computed: {
    baseStyles () {
      return [
        this.currentSite.themeColorsCss,
        this.currentSite.transformedStyles || ''
      ].join(' ')
    },
    foundNetlifyUrl () {
      return this.$mh.foundNetlifyUrl(this.globalWidget.content)
    }
  },
  watch: {
    'globalWidget.type': {
      handler () {
        // Using removeAll here prevents revert from resetting the globalWidgetBlocks unless clicked a second time
        _.each(this.globalWidget.globalWidgetBlocks.data, (globalWidgetBlock) => {
          if (globalWidgetBlock.type !== this.globalWidget.type) this.globalWidget.globalWidgetBlocks.remove(globalWidgetBlock)
        })
      }
    }
  },
  created () {
    _.extend(this, { GLOBAL_WIDGET_TYPES })
  },
  methods: {
    show (model, initialData) {
      this.$refs.modelForm.show(model, initialData)
    },
    cancel () {
      this.$refs.modelForm.revert()
      this.$emit('cancel')
    }
  }
}
</script>
